/**
 * TanStack Query hooks for platform administration. Keys are namespaced
 * "platform/…". Lists that a write can change are invalidated by their `all`
 * key, so an approval decided in the detail panel disappears from the inbox
 * without the screen reaching into the cache to edit a row by hand.
 */
import { useQuery } from "@tanstack/react-query";

import {
  getApproval,
  getBranch,
  getNotificationPreferences,
  listApprovals,
  listBranches,
  listNotifications,
  type Approval,
  type ApprovalDetail,
  type ApprovalFilters,
  type Branch,
  type BranchStatus,
  type NotificationPage,
} from "./api";

export const platformKeys = {
  approvals: {
    all: ["platform/approvals"] as const,
    list: (filters: Record<string, unknown>) => ["platform/approvals", "list", filters] as const,
    detail: (approvalId: string) => ["platform/approvals", "detail", approvalId] as const,
  },
  notifications: {
    all: ["platform/notifications"] as const,
    list: (unread: boolean) => ["platform/notifications", "list", unread] as const,
    preferences: () => ["platform/notifications", "preferences"] as const,
  },
  branches: {
    all: ["platform/branches"] as const,
    list: (status: string) => ["platform/branches", "list", status] as const,
    detail: (branchId: string) => ["platform/branches", "detail", branchId] as const,
  },
};

const APPROVAL_PAGE = 50;
const NOTIFICATION_PAGE = 50;

export function useApprovals(filters: ApprovalFilters) {
  return useQuery<Approval[]>({
    queryKey: platformKeys.approvals.list({ ...filters }),
    queryFn: async ({ signal }) => [
      ...(await listApprovals(filters, APPROVAL_PAGE, undefined, signal)).items,
    ],
  });
}

export function useApproval(approvalId: string | null) {
  return useQuery<ApprovalDetail>({
    queryKey: platformKeys.approvals.detail(approvalId ?? ""),
    queryFn: ({ signal }) => getApproval(approvalId as string, signal),
    enabled: approvalId !== null && approvalId !== "",
  });
}

export function useNotifications(unread: boolean) {
  return useQuery<NotificationPage>({
    queryKey: platformKeys.notifications.list(unread),
    queryFn: ({ signal }) => listNotifications(unread, NOTIFICATION_PAGE, undefined, signal),
  });
}

export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: platformKeys.notifications.preferences(),
    queryFn: ({ signal }) => getNotificationPreferences(signal),
    enabled,
  });
}

export function useBranches(status: BranchStatus | "all") {
  return useQuery<Branch[]>({
    queryKey: platformKeys.branches.list(status),
    queryFn: ({ signal }) => listBranches(status === "all" ? {} : { status }, signal),
  });
}

/** Body + ETag snapshot; the snapshot validates the next PATCH. */
export function useBranchDetail(branchId: string | null) {
  return useQuery({
    queryKey: platformKeys.branches.detail(branchId ?? ""),
    queryFn: ({ signal }) => getBranch(branchId as string, signal),
    enabled: branchId !== null && branchId !== "",
  });
}
